import React, { useMemo } from "react";
import { isEmpty } from "lodash";

import { Icon } from "orcs-design-system";

import { useLocation, matchPath } from "react-router";
import styled from "styled-components";
import { useDateSettings } from "src/contexts/global/ApolloSettingsContext";
import { useSidebarGuidedTour } from "src/contexts/global/GuidedTourContext";
import {
  useActiveFilterCount,
  useFteMode,
} from "src/contexts/filterAndFte/FilterAndFteContext";

import useGuidedTourTrigger from "src/guidedTour/useGuidedTourTrigger";
import useTourForRouteMatch from "src/guidedTour/useTourForRouteMatch";
import {
  isPeoplePage,
  isSummaryPage,
  isSearchPage,
  isTeamAllocationsPage,
  isStrategyPage,
} from "src/util/url";
import useDefaultRoutePath from "src/app/Restricted/useDefaultRoutePath";

import Url, { matchPaths } from "src/consts/urlPaths";

import WorkspaceAnnouncements from "src/components/PersonDetailPage/WorkspaceAnnouncements";
import {
  useWorkspace,
  useWorkspaceFeatureFlags,
} from "src/contexts/global/WorkspaceContext";
import { isOrgDesignBetaEnabled } from "src/components/OrgChart/controller/dnd/featureFlags";
import Restricted from "src/app/Restricted";
import useRestricted from "src/app/Restricted/useRestricted";
import useUserRole from "src/app/Restricted/useUserRole";
import {
  ADMIN_ROLE,
  ALLOWED_ROLES,
  getFromUserOrAuth,
  isInternalSupportUser,
} from "src/services/auth";
import { useOrgFeatureFlagsForTenant } from "src/hooks/orgConfig/hooks/useOrgFeatureFlagsForTenant";
import { canPerform } from "src/components/Can";
import useShowInvite from "src/components/InviteUser/hooks/useShowInvite";
import useGroupsNavigationData from "src/components/GroupsNavigation/useGroupsNavigationData";
import GroupsNavigationLoader from "src/components/GroupsNavigation";
import TagsNavigation from "src/components/TagsNavigation";
import InviteUser from "src/components/InviteUser";
import { trackEvent } from "src/services/segment";
import { EVENT_TRACKING, EVENT_LOCATIONS } from "src/consts/eventTracking";
import ExploreMenu from "src/components/ExploreMenu";
import InfoSidebarContent from "./components/InfoSidebarContent";
import AIDashboardsContent from "./components/AIDashboardsContent";
import FilterSidebarContent from "./components/FilterSidebarContent";
import FilterSearchResults from "./components/FilterSearchResults";
import HistoricalDataSidebarContent from "./components/HistoricalDataSidebarContent";
import FteDataSidebarContent from "./components/FteDataSidebarContent";
import SettingsContent from "./components/SettingsContent";
import AppSwitcherContent from "./components/AppSwitcherContent";
import HomeLink from "./components/HomeLink";
import SummaryLink from "./components/SummaryLink";
import PeopleLink from "./components/PeopleLink";
import WorkforceLink from "./components/WorkforceLink";
import DesignsLink from "./components/DesignsLink";
import StrategyInsights from "./components/StrategyInsights";
import TeamCanvasContent from "./components/TeamCanvasContent";

const CustomIcon = styled(Icon)`
  path {
    fill: white !important;
  }
`;

const ITEM_NAMES = {
  HOME: "Home",
  EXPLORE: "Explore",
  BROWSE_TEAMS: "Explore",
  TAGS: "Tags",
  PEOPLE: "People",
  WORKFORCE: "Workforce",
  INSIGHTS: "Insights",
  TEAM_CANVAS: "Team Canvas",
  AI_DASHBOARDS: "Dashboards",
  POINT_IN_TIME: "Point in time",
  APPS: "Apps",
  FILTER: "Filter",
  FILTER_SEARCH_RESULTS: "Filter search results",
  TEAM_ALIGNMENT: "Team alignment",
  TOGGLE_FTE: "Toggle FTE",
  GUIDED_TOUR: "Guided tour",
  DESIGNS: "Designs",
  SETTINGS: "Settings",
  INVITE: "Invite",
  GET_HELP: "Get help",
  LOGOUT: "Logout",
};

const useSideNavItemBuilder = ({
  clickedItem,
  user,
  tagTypesConfig,
  logout,
  featureFlags,
  eventEmitter,
  setClickedItem,
}) => {
  // Get workspace communications heading
  const workspace = useWorkspace();
  const shouldShowInvite = useShowInvite({ workspace, user });
  const activeFilterCount = useActiveFilterCount();
  const fteMode = useFteMode();

  const match = matchPaths();

  const dateSettings = useDateSettings();
  const { availableInSidebar } = useSidebarGuidedTour();

  const tourName = useTourForRouteMatch();
  const triggerGuidedTour = useGuidedTourTrigger();
  const tourAvailable = availableInSidebar?.[tourName];

  const [isRestricted, isPartiallyRestricted] = useRestricted();
  const isAdmin = useUserRole() === ADMIN_ROLE;
  const userRoles = getFromUserOrAuth(user, "roles", []);
  const { featureFlags: orgFeatureFlags } = useOrgFeatureFlagsForTenant();
  const enableDesigns = orgFeatureFlags?.designs?.enableDesigns ?? false;
  const showDesigns =
    (canPerform(user, userRoles, ALLOWED_ROLES.DESIGNS) ||
      isInternalSupportUser(user)) &&
    enableDesigns;
  const {
    enableTagExplorer,
    enableAIChat,
    enableOrgDesign,
    enableDemoODOptions,
    enableTeamCanvas,
    enablePositionBackedVacancies,
  } = useWorkspaceFeatureFlags();
  const showWorkforce =
    isAdmin && isInternalSupportUser(user) && !!enablePositionBackedVacancies;

  const { panelHeading, notifications } =
    workspace?.config?.notifications?.landingPageNotifications || {};
  const heading = panelHeading || "Announcements";
  const showDot = !isEmpty(notifications);

  const groupsNavProps = useGroupsNavigationData({
    workspace,
    featureFlags,
    isSelected:
      clickedItem?.name === ITEM_NAMES.BROWSE_TEAMS ||
      clickedItem?.name === ITEM_NAMES.EXPLORE,
    eventEmitter,
  });

  const searchFilterBadge = (type) => {
    switch (type) {
      case "all":
        return null;
      case "people":
        return (
          <CustomIcon
            icon={["fas", "user"]}
            title="People"
            size="xs"
            transform="right-1"
          />
        );
      case "positions":
        return (
          <CustomIcon
            icon={["far", "id-badge"]}
            title="Positions"
            size="xs"
            transform="right-1"
          />
        );
      case "teams":
        return (
          <CustomIcon
            icon={["fas", "users"]}
            title="Teams"
            size="xs"
            transform="right-1"
          />
        );
      case "tags":
        return (
          <CustomIcon
            icon={["fas", "tag"]}
            title="Tags"
            size="xs"
            transform="right-1 down-1"
          />
        );
      default:
        return "";
    }
  };
  const location = useLocation();
  const defaultRoutePath = useDefaultRoutePath();

  const sidebarTabs = useMemo(() => {
    return [
      {
        iconName: "home",
        name: ITEM_NAMES.HOME,
        component: HomeLink,
        actionType: "link",
        isActive: !!matchPath(location.pathname, {
          path: defaultRoutePath,
          exact: true,
        }),
        onClick: () => {
          trackEvent(EVENT_TRACKING.URL_CLICKED, {
            action: "home_sidebar_selected",
          });
        },
      },
      {
        iconName: "layer-group",
        name: ITEM_NAMES.EXPLORE,
        component: () => <ExploreMenu groupsNavProps={groupsNavProps} />,
        onClick: (item) => {
          if (clickedItem?.name !== ITEM_NAMES.EXPLORE) {
            setClickedItem(item);
          }
        },
        large: true,
        hide: !enableTagExplorer,
        actionType: "component",
      },
      {
        iconName: "layer-group",
        name: ITEM_NAMES.BROWSE_TEAMS,
        component: () => <GroupsNavigationLoader {...groupsNavProps} />,
        onClick: (item) => {
          if (clickedItem?.name !== ITEM_NAMES.BROWSE_TEAMS) {
            setClickedItem(item);
          }
        },
        large: true,
        hide: enableTagExplorer,
        actionType: "component",
      },
      {
        iconName: "tag",
        name: ITEM_NAMES.TAGS,
        component: () => (
          <TagsNavigation
            tagTypesConfig={tagTypesConfig}
            selectedTab={
              clickedItem?.name === ITEM_NAMES.TAGS ? "tagsTree" : ""
            }
          />
        ),
        onClick: (item) => {
          if (clickedItem?.name !== ITEM_NAMES.TAGS) {
            setClickedItem(item);
          }
        },
        actionType: "component",
      },
      {
        iconName: "users",
        name: ITEM_NAMES.PEOPLE,
        component: PeopleLink,
        actionType: "link",
        hide: isRestricted,
        isActive: !!matchPath(location.pathname, {
          path: Url.PEOPLE,
          exact: true,
        }),
      },
      {
        iconName: "clipboard-check",
        name: ITEM_NAMES.WORKFORCE,
        component: WorkforceLink,
        actionType: "link",
        hide: isRestricted || !showWorkforce,
        isActive: !!matchPath(location.pathname, {
          path: Url.WORKFORCE_ROOT,
          exact: false,
        }),
      },
      {
        iconName: "chart-line",
        name: ITEM_NAMES.INSIGHTS,
        component: SummaryLink,
        actionType: "link",
        hide: isRestricted,
        isActive: !!matchPath(location.pathname, {
          path: Url.INSIGHTS,
          exact: true,
        }),
        onClick: () => {
          trackEvent(EVENT_TRACKING.INSIGHTS_CLICKED, {
            location: EVENT_LOCATIONS.NAV_BAR,
          });
        },
      },
      {
        iconName: "sitemap",
        name: ITEM_NAMES.TEAM_CANVAS,
        component: () => <TeamCanvasContent />,
        actionType: "component",
        hide:
          !isOrgDesignBetaEnabled({ enableOrgDesign, enableDemoODOptions }) ||
          !enableTeamCanvas ||
          isRestricted,
        isActive: !!matchPath(location.pathname, {
          path: Url.TEAM_CANVAS,
          exact: true,
        }),
      },
      {
        iconName: "sparkles",
        name: ITEM_NAMES.AI_DASHBOARDS,
        component: () => <AIDashboardsContent />,
        actionType: "component",
        hide: !enableAIChat,
      },
      {
        iconName: "history",
        name: ITEM_NAMES.POINT_IN_TIME,
        hide: isTeamAllocationsPage(match) || isRestricted,
        component: () => (
          <Restricted>
            <HistoricalDataSidebarContent />
          </Restricted>
        ),
        badgeNumber: isEmpty(dateSettings) ? null : "!",
        actionType: "component",
        bottomAligned: true,
      },
      {
        iconName: "external-link",
        name: ITEM_NAMES.APPS,
        component: () => <AppSwitcherContent />,
        actionType: "component",
        bottomAligned: true,
      },
      {
        iconName: "filter",
        name: ITEM_NAMES.FILTER,
        hide: !isSummaryPage(match?.path) || isRestricted,
        pageSpecific: true,
        component: () => <FilterSidebarContent />,
        ...(activeFilterCount !== 0 && {
          badgeNumber: activeFilterCount,
        }),
        isExpandedByDefault: matchPath(location.pathname, {
          path: Url.INSIGHTS,
        }),
        actionType: "component",
      },
      {
        iconName: "filter",
        name: ITEM_NAMES.FILTER,
        hide: !isPeoplePage(match?.path) || isRestricted,
        pageSpecific: true,
        component: () => <FilterSidebarContent />,
        ...(activeFilterCount !== 0 && {
          badgeNumber: activeFilterCount,
        }),
        isExpandedByDefault: matchPath(location.pathname, {
          path: Url.PEOPLE,
          exact: true,
        }),
        actionType: "component",
      },
      {
        iconName: "filter",
        isExpandedByDefault: matchPath(location.pathname, {
          path: Url.SEARCH,
          exact: false,
        }),
        name: ITEM_NAMES.FILTER_SEARCH_RESULTS,
        hide: !isSearchPage(match?.path),
        pageSpecific: true,
        badgeNumber: searchFilterBadge(match?.params?.type),
        component: () => <FilterSearchResults match={match} />,
        actionType: "component",
        isActive: matchPath(location.pathname, {
          path: "/search",
          exact: false,
        }),
      },
      {
        iconName: "bolt",
        name: ITEM_NAMES.TEAM_ALIGNMENT,
        isExpandedByDefault: true,
        hide: !isStrategyPage(match?.params),
        pageSpecific: true,
        component: () => (
          <Restricted>
            <StrategyInsights />
          </Restricted>
        ),
        actionType: "component",
      },
      {
        iconName: "people-arrows",
        name: ITEM_NAMES.TOGGLE_FTE,
        hide: !isSummaryPage(match?.path) || isRestricted,
        pageSpecific: true,
        component: () => (
          <Restricted>
            <FteDataSidebarContent />
          </Restricted>
        ),
        badgeNumber: fteMode ? "!" : null,
        actionType: "component",
      },
      {
        iconName: "play-circle",
        name: ITEM_NAMES.GUIDED_TOUR,
        hide:
          isSummaryPage(match?.path) ||
          !(tourName && tourAvailable) ||
          isRestricted,
        actionType: "button",
        onClick: () => triggerGuidedTour(tourName),
        adminOnly: true,
        bottomAligned: true,
      },
      {
        iconName: "bell",
        name: heading,
        bottomAligned: true,
        badgeDot: showDot,
        component: () => (
          <Restricted>
            <WorkspaceAnnouncements inSideNav={true} />
          </Restricted>
        ),
        actionType: "component",
        onClick: () => {
          trackEvent(EVENT_TRACKING.URL_CLICKED, {
            action: "announcements_sidebar_selected",
          });
        },
        hide: isRestricted,
      },
      {
        iconName: "project-diagram",
        name: ITEM_NAMES.DESIGNS,
        component: DesignsLink,
        actionType: "link",
        bottomAligned: true,
        isActive: !!matchPath(location.pathname, {
          path: Url.DESIGNS,
          exact: false,
        }),
        hide: isRestricted || !showDesigns,
      },
      {
        iconName: "cog",
        name: ITEM_NAMES.SETTINGS,
        bottomAligned: true,
        component: () => (
          <Restricted>
            <SettingsContent />
          </Restricted>
        ),
        actionType: "component",
        hide: isRestricted || isPartiallyRestricted,
      },
      {
        iconName: "user-plus",
        name: ITEM_NAMES.INVITE,
        hide: !shouldShowInvite,
        large: true,
        bottomAligned: true,
        component: () => <InviteUser workspace={workspace} user={user} />,
        actionType: "component",
      },
      {
        iconName: "question-circle",
        name: ITEM_NAMES.GET_HELP,
        bottomAligned: true,
        component: () => <InfoSidebarContent />,
        actionType: "component",
      },
      {
        iconName: "lock",
        name: ITEM_NAMES.LOGOUT,
        bottomAligned: true,
        onClick: logout,
        actionType: "button",
        hide: !logout,
      },
    ];
  }, [
    activeFilterCount,
    dateSettings,
    fteMode,
    groupsNavProps,
    heading,
    isPartiallyRestricted,
    showDesigns,
    showWorkforce,
    user,
    isRestricted,
    logout,
    match,
    setClickedItem,
    shouldShowInvite,
    showDot,
    tagTypesConfig,
    tourAvailable,
    tourName,
    triggerGuidedTour,
    workspace,
    location,
    defaultRoutePath,
    enableTagExplorer,
    enableOrgDesign,
    enableDemoODOptions,
    enableTeamCanvas,
    enableAIChat,
    clickedItem,
  ]);

  return sidebarTabs;
};

export default useSideNavItemBuilder;
